/**
 * Scheda di monitoraggio di un atleta sul periodo (livello 2 della maschera,
 * rotta `/monitoring/:athleteId`, D32): una sola barra di tab.
 *   - Giorno per giorno, dal piu' recente come il Diario; `initialDay` (dalla
 *     cella del registro) porta quel giorno in vista e lo evidenzia.
 *   - Andamenti (`AthleteTrends`): gli sport stanno li' dentro, non sono tab.
 *   - Benessere.
 * Legge `GET /coach/monitoring/athletes/{id}`; i valori del benessere arrivano
 * dalla rotta wellness esistente, che ha gia' la sua guardia.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	ArrowLeft,
	BookOpen,
	MessageSquare,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Circle,
	ExternalLink,
	Loader2,
	Plus,
} from "lucide-react";
import { fetchAPI, formatFullName } from "../../lib/utils";
import { formatExercisePrescription } from "../../lib/programBuilderUtils";
import {
	collapseRestDays,
	coverageSentence,
	daysSinceLabel,
	formatQuantity,
	formatShortDay,
	qualityLabelKey,
	weekdayShort,
} from "../../lib/monitoringUtils";
import { exerciseForResult } from "../../pages/workouts/lib/dayAggregator";
import { ExerciseDetailAccordion } from "../../pages/workouts/components/ExerciseDetailAccordion";
import { WellnessCharts } from "../wellness/WellnessCharts";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { AthleteTrends } from "./AthleteTrends";
import { FlagChip, StatusBadge, useIsCompact } from "./primitives";

function quantitySummary(quantities, t, language) {
	const parts = [];
	(quantities || []).forEach((quantity) => {
		if (quantity.value === null || quantity.value === undefined) return;
		let text = formatQuantity(quantity.value, quantity.unit, language);
		if (quantity.tonnage_kg) text += ` · ${formatQuantity(quantity.tonnage_kg, "kg", language)}`;
		if (quantity.quality && quantity.quality !== "recorded") text += ` (${t(qualityLabelKey(quantity.quality))})`;
		parts.push(quantity.exercise_name && quantities.length > 1 ? `${quantity.exercise_name}: ${text}` : text);
	});
	return parts.join(" · ");
}

/** Il giorno in cui un risultato e' stato scritto, se diverso da quello della giornata: un recupero si vede, non si nasconde. */
function loggedOnLabel(results, dayDate, t, language) {
	const other = (results || [])
		.map((result) => String(result?.created_at || "").slice(0, 10))
		.filter((day) => /^\d{4}-\d{2}-\d{2}$/.test(day) && day !== dayDate);
	if (!other.length) return "";
	return ` · ${t("monLoggedOn")} ${formatShortDay(other[0], language)}`;
}

function ExerciseRow({ row, status, dayDate, t, language }) {
	const [open, setOpen] = useState(false);
	const exercise = row.exercise || {};
	const logged = Boolean(row.logged);
	const summary =
		quantitySummary(row.quantities, t, language) +
		(logged ? loggedOnLabel(row.results, dayDate, t, language) : "");
	const planned = formatExercisePrescription(exercise, t);
	const Icon = logged ? CheckCircle2 : Circle;
	const iconClass = logged ? "text-volt" : status === "upcoming" ? "text-muted-foreground" : "text-blaze";
	return (
		<div className="rounded-sm border border-border/60 bg-background/30">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="w-full flex items-start gap-3 px-3 py-2 text-left"
				aria-expanded={open}
			>
				<Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} aria-hidden="true" />
				<div className="flex-1 min-w-0">
					<div className="text-sm font-medium truncate">
						{exercise.exercise_name || "—"}
						{exercise._session_label ? (
							<span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
								{exercise._session_label}
							</span>
						) : null}
					</div>
					<div className="text-xs text-muted-foreground truncate">
						{t("monPlanned")}: {planned || "—"}
					</div>
					<div className={`text-xs ${logged ? "" : "text-muted-foreground"}`}>
						{t("monDone")}:{" "}
						{logged
							? summary || t("monLoggedWithoutQuantity")
							: status === "upcoming"
								? "—"
								: t("monNotLogged")}
					</div>
				</div>
				{open ? (
					<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
				) : (
					<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
				)}
			</button>
			{open ? (
				<div className="border-t border-border/60">
					{logged ? (
						row.results.map((result) => (
							<ExerciseDetailAccordion
								key={result.result_id}
								exercise={{ ...exercise, result }}
								language={language}
								isCoachView
							/>
						))
					) : (
						<ExerciseDetailAccordion exercise={exercise} language={language} isCoachView />
					)}
				</div>
			) : null}
		</div>
	);
}

function ExtraResultRow({ result, t, language }) {
	const [open, setOpen] = useState(false);
	const exercise = exerciseForResult(result);
	return (
		<div className="rounded-sm border border-sky-400/30 bg-sky-400/5">
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="w-full flex items-center gap-3 px-3 py-2 text-left"
				aria-expanded={open}
			>
				<Plus className="w-4 h-4 text-sky-600 dark:text-sky-400 shrink-0" aria-hidden="true" />
				<div className="flex-1 min-w-0">
					<div className="text-sm font-medium truncate">{result.exercise_name || "—"}</div>
					<div className="text-xs text-muted-foreground truncate">
						{result.program_title
							? `${t(result.undated_program ? "monUndatedProgramResult" : "monOtherDayProgramResult")} · ${result.program_title}`
							: t("monExtraResults")}
					</div>
				</div>
				{open ? (
					<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
				) : (
					<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
				)}
			</button>
			{open ? (
				<div className="border-t border-border/60">
					<ExerciseDetailAccordion exercise={exercise} language={language} isCoachView />
				</div>
			) : null}
		</div>
	);
}

function DayCard({ day, highlighted, t, language }) {
	const status = day.cell?.status || (day.kind === "extra" ? "rest" : day.kind);
	return (
		<div
			id={`mon-day-${day.date}`}
			className={`rounded-sm border bg-card scroll-mt-24 ${highlighted ? "border-volt ring-2 ring-volt/30" : "border-border"}`}
			data-testid={`mon-day-${day.date}`}
		>
			<div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/60">
				<span className="text-[10px] uppercase tracking-widest text-muted-foreground w-8">
					{weekdayShort(day.date, language)}
				</span>
				<span className="font-heading text-lg font-bold leading-none">{formatShortDay(day.date, language)}</span>
				{day.kind !== "extra" ? <StatusBadge status={status} t={t} /> : null}
				{day.cell?.extra || day.cell?.diary ? (
					<span className="inline-flex items-center gap-1 rounded-sm border border-sky-400/40 bg-sky-400/10 px-1.5 py-0.5 text-[11px] text-sky-600 dark:text-sky-400">
						<Plus className="w-3 h-3" aria-hidden="true" />
						{t("monLegendExtra")}
					</span>
				) : null}
			</div>
			<div className="px-3 py-3 space-y-4">
				{day.assigned.map((assigned) => (
					<div key={`${assigned.program_id}:${assigned.day_id}`} className="space-y-2">
						<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
							<span className="font-medium">{assigned.program_title}</span>
							{assigned.day_title ? <span className="text-muted-foreground">· {assigned.day_title}</span> : null}
							{assigned.week_label ? <span className="text-muted-foreground">· {assigned.week_label}</span> : null}
							<span className="text-muted-foreground tabular-nums">
								· {assigned.logged}/{assigned.prescribed} {t("monExercisesLogged")}
							</span>
						</div>
						<div className="space-y-1.5">
							{assigned.exercises.map((row, index) => (
								<ExerciseRow
									key={`${row.exercise?.exercise_id || row.exercise?.exercise_name || "ex"}-${index}`}
									row={row}
									status={assigned.status}
									dayDate={day.date}
									t={t}
									language={language}
								/>
							))}
						</div>
						{assigned.unmatched_results?.length ? (
							<div className="space-y-1.5">
								<div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("monUnmatched")}</div>
								{assigned.unmatched_results.map((result) => (
									<ExtraResultRow key={result.result_id} result={result} t={t} language={language} />
								))}
							</div>
						) : null}
					</div>
				))}
				{day.extra_results?.length ? (
					<div className="space-y-1.5">
						{day.extra_results.map((result) => (
							<ExtraResultRow key={result.result_id} result={result} t={t} language={language} />
						))}
					</div>
				) : null}
				{day.diary_entries?.length ? (
					<div className="space-y-1.5">
						{day.diary_entries.map((entry) => (
							<div
								key={entry.entry_id}
								className="flex items-start gap-3 rounded-sm border border-border/60 px-3 py-2"
							>
								<BookOpen className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
								<div className="min-w-0 text-sm">
									<div className="font-medium">
										{entry.title}
										<span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
											{t("monDiaryEntry")}
										</span>
									</div>
									{entry.description ? (
										<div className="text-xs text-muted-foreground whitespace-pre-wrap">{entry.description}</div>
									) : null}
									{entry.duration_minutes ? (
										<div className="text-xs text-muted-foreground">{entry.duration_minutes} min</div>
									) : null}
								</div>
							</div>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
}

const TABS = ["days", "trends", "wellness"];

export function AthleteMonitoringPanel({
	athleteId,
	range,
	periodLabel,
	today,
	programId,
	initialDay = null,
	tab = "days",
	onTabChange,
	onLoaded,
	onBack,
	filters = null,
	t,
	language,
}) {
	const navigate = useNavigate();
	const compact = useIsCompact();
	const rootRef = useRef(null);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const [wellness, setWellness] = useState({ catalog: [], entries: [] });
	// Etichette degli sport: dato admin, non si traduce.
	const [sportLabels, setSportLabels] = useState([]);
	const activeTab = TABS.includes(tab) ? tab : "days";

	useEffect(() => {
		fetchAPI("/users/sports/list")
			.then((rows) => setSportLabels(Array.isArray(rows) ? rows : []))
			.catch(() => setSportLabels([]));
	}, []);

	const sports = data?.athlete?.sports || [];

	const load = useCallback(async () => {
		setLoading(true);
		setFailed(false);
		try {
			const params = new URLSearchParams({ date_from: range.from, date_to: range.to, today });
			if (programId && programId !== "all") params.set("program_id", programId);
			const payload = await fetchAPI(`/coach/monitoring/athletes/${athleteId}?${params.toString()}`);
			setData(payload);
			onLoaded?.(payload);
			if (payload?.wellness?.enabled) {
				const [catalog, entries] = await Promise.all([
					fetchAPI("/wellness/catalog").catch(() => []),
					fetchAPI(
						`/wellness/athletes/${athleteId}/entries?date_from=${range.from}&date_to=${range.to}`,
					).catch(() => ({ entries: [] })),
				]);
				setWellness({
					catalog: Array.isArray(catalog) ? catalog : catalog?.fields || [],
					entries: entries?.entries || [],
				});
			} else {
				setWellness({ catalog: [], entries: [] });
			}
		} catch (error) {
			setFailed(true);
			toast.error(error?.message || t("monLoadError"));
		} finally {
			setLoading(false);
		}
		// onLoaded e' un callback del genitore: non deve rifare la lettura.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [athleteId, range.from, range.to, today, programId, t]);

	useEffect(() => {
		load();
	}, [load]);

	const summary = data?.summary;
	// Il piu' recente in cima, come il Diario: «ieri» non sta in fondo alla pagina.
	// Si compatta in ordine di calendario, poi si gira, cosi' i tratti di riposo
	// restano scritti «dal – al».
	const timeline = useMemo(() => collapseRestDays(data?.days || []).reverse(), [data]);

	// Aprendo un atleta la pagina torna all'inizio della scheda; arrivando da una
	// cella del registro va a quel giorno.
	useEffect(() => {
		if (!data) return;
		const target = initialDay && activeTab === "days" ? document.getElementById(`mon-day-${initialDay}`) : null;
		(target || rootRef.current)?.scrollIntoView?.({ block: "start" });
		// Solo all'arrivo dei dati di questo atleta, non a ogni cambio di tab.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, initialDay]);

	const writeToAthlete = () =>
		navigate("/messages", {
			state: { contact: { user_id: athleteId, name: data ? formatFullName(data.athlete) : "" } },
		});

	return (
		<div ref={rootRef} className="space-y-4 scroll-mt-20" data-testid="monitoring-athlete-panel">
			{/* Sul telefono i tasti stanno sulla prima riga e il nome sulla sua: in tre su una riga il nome restava di poche lettere. */}
			<div className="flex flex-wrap items-start gap-x-3 gap-y-2">
				<Button variant="outline" size="sm" className="rounded-sm" onClick={onBack} data-testid="mon-back">
					<ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
					{t("monBackToList")}
				</Button>
				<div className="order-last basis-full min-w-0 md:order-none md:basis-auto md:flex-1">
					<h1 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight break-words">
						{data ? formatFullName(data.athlete) : "…"}
					</h1>
					{data?.athlete?.email ? (
						<div className="text-xs text-muted-foreground truncate">{data.athlete.email}</div>
					) : null}
				</div>
				<div className="flex items-center gap-2 ml-auto md:ml-0">
					<Button
						variant="outline"
						size="sm"
						className="rounded-sm"
						onClick={writeToAthlete}
						disabled={!data}
						data-testid="mon-write-athlete"
					>
						<MessageSquare className="w-4 h-4 mr-1" aria-hidden="true" />
						{t("monWriteTo")}
					</Button>
					<Button
						variant="outline"
						size="sm"
						className="rounded-sm"
						onClick={() => navigate(`/athletes/${athleteId}?tab=results`)}
						aria-label={t("monOpenAthlete")}
					>
						<ExternalLink className="w-4 h-4 mr-1" aria-hidden="true" />
						{t(compact ? "monOpenAthleteShort" : "monOpenAthlete")}
					</Button>
				</div>
			</div>

			{filters}

			{failed && !data ? (
				<div className="rounded-sm border border-blaze/30 bg-blaze/10 px-4 py-3 text-sm text-blaze flex flex-wrap items-center justify-between gap-2">
					<span>{t("monAthleteLoadError")}</span>
					<span className="flex gap-2">
						<Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={load}>
							{t("tryAgain")}
						</Button>
						<Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={onBack}>
							{t("monBackToList")}
						</Button>
					</span>
				</div>
			) : null}

			{summary ? (
				<div className="rounded-sm border border-border bg-card px-4 py-3 space-y-2" data-testid="mon-athlete-summary">
					<div className="text-sm">
						{periodLabel ? <span className="text-muted-foreground">{periodLabel}: </span> : null}
						<span className="font-medium">{coverageSentence(summary, t)}</span>
						<span className="text-muted-foreground">
							{" "}· {t("monColLastActivity").toLowerCase()}: {daysSinceLabel(summary, t)}
						</span>
						{summary.extra_days ? (
							<span className="text-muted-foreground">
								{" "}· {summary.extra_days} {t("monExtraDays")}
							</span>
						) : null}
					</div>
					{summary.flags?.length ? (
						<div className="flex flex-wrap gap-1">
							{summary.flags.map((flag) => (
								<FlagChip key={flag.code} flag={flag} t={t} language={language} />
							))}
						</div>
					) : null}
					{summary.undated_programs ? (
						<p className="text-xs text-muted-foreground">{t("monUndatedPrograms")}</p>
					) : null}
				</div>
			) : null}

			<Tabs value={activeTab} onValueChange={(next) => onTabChange?.(next)} className="w-full">
				<TabsList className="rounded-sm grid w-full grid-cols-3 md:inline-flex md:w-auto">
					<TabsTrigger value="days" className="rounded-sm min-w-0">
						<span className="truncate">{t(compact ? "monSectionDaysShort" : "monSectionDays")}</span>
					</TabsTrigger>
					<TabsTrigger value="trends" className="rounded-sm min-w-0">
						<span className="truncate">{t("monSectionTrends")}</span>
					</TabsTrigger>
					<TabsTrigger value="wellness" className="rounded-sm min-w-0">
						<span className="truncate">{t("monSectionWellness")}</span>
					</TabsTrigger>
				</TabsList>
				<div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
					<TabsContent value="days" className="mt-3 space-y-3">
						{loading && !data ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
								<Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
								{t("loading")}
							</div>
						) : null}
						{timeline.map((item) =>
							item.kind === "rest_run" ? (
								<div
									key={`rest-${item.from}`}
									className="flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground"
								>
									<span className="h-px flex-1 bg-border" aria-hidden="true" />
									<span>
										{item.count} {t("monRestRun")}
										{item.count > 1
											? ` (${formatShortDay(item.from, language)} – ${formatShortDay(item.to, language)})`
											: ` (${formatShortDay(item.from, language)})`}
									</span>
									<span className="h-px flex-1 bg-border" aria-hidden="true" />
								</div>
							) : (
								<DayCard
									key={item.date}
									day={item}
									highlighted={item.date === initialDay}
									t={t}
									language={language}
								/>
							),
						)}
					</TabsContent>
					<TabsContent value="trends" className="mt-3">
						{data ? (
							<AthleteTrends
								athleteId={athleteId}
								sports={sports}
								sportLabels={sportLabels}
								today={today}
								programId={programId}
								t={t}
								language={language}
							/>
						) : null}
					</TabsContent>
					<TabsContent value="wellness" className="mt-3 space-y-3">
						{!data?.wellness?.enabled ? (
							<p className="text-sm text-muted-foreground py-4">{t("monWellnessOffPanel")}</p>
						) : (
							<>
								<p className="text-sm">
									{data.wellness.entries}/{data.wellness.days_in_window} {t("monWellnessCheckins")}
									{data.wellness.last_entry_date
										? ` · ${t("monWellnessLast")} ${formatShortDay(data.wellness.last_entry_date, language)}`
										: ""}
								</p>
								<WellnessCharts
									entries={wellness.entries}
									catalog={wellness.catalog}
									fields={data.wellness.fields || []}
								/>
							</>
						)}
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
}
